/**
 * Progressive Skeleton Loading Component
 * Shows skeleton components with staggered animations for a more natural loading experience
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { DynamicSkeleton, SkeletonProps } from "./dynamic-skeleton";
import { cn } from "./utils";

// Re-export components for convenience
export { DynamicSkeleton, SkeletonContainer } from "./dynamic-skeleton";

interface ProgressiveSkeletonProps {
  /** Array of skeleton configurations */
  skeletons: Array<{
    id: string;
    props: SkeletonProps;
    delay?: number; // Delay in ms before showing this skeleton
  }>;
  /** Total loading duration in ms */
  duration?: number;
  /** Custom className for the container */
  className?: string;
  /** Whether to show progressive loading */
  progressive?: boolean;
}

/**
 * Progressive skeleton component that shows skeletons with staggered timing
 */
export function ProgressiveSkeleton({
  skeletons,
  duration = 2000,
  className,
  progressive = true,
}: ProgressiveSkeletonProps) {
  const [visibleSkeletons, setVisibleSkeletons] = useState<Set<string>>(
    new Set(),
  );

  // Derive a stable dependency key from IDs only to avoid rerunning
  // when parent recreates the array with the same items each render.
  const depKey = useMemo(
    () => skeletons.map((s) => s.id).join("|"),
    [skeletons],
  );

  // Track timers so we can cancel them on cleanup
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    if (!progressive) {
      // Show all skeletons immediately
      setVisibleSkeletons(new Set(skeletons.map((s) => s.id)));
      return;
    }

    // Calculate stagger timing
    const totalSkeletons = skeletons.length;
    const staggerInterval = duration / totalSkeletons;

    // Show skeletons progressively
    skeletons.forEach((skeleton, index) => {
      const delay = skeleton.delay || index * staggerInterval;
      const t = setTimeout(() => {
        setVisibleSkeletons((prev) => {
          if (prev.has(skeleton.id)) return prev; // no-op to avoid extra renders
          const next = new Set(prev);
          next.add(skeleton.id);
          return next;
        });
      }, delay);
      timersRef.current.push(t);
    });

    // Cleanup function
    return () => {
      // Clear any pending timers to avoid setting state after unmount
      for (const t of timersRef.current) clearTimeout(t);
      timersRef.current = [];
      setVisibleSkeletons(new Set());
    };
    // Depend on the derived key to avoid thrashing when array identity changes
  }, [depKey, duration, progressive]);

  return (
    <div className={cn("space-y-4", className)}>
      {skeletons.map((skeleton) => (
        <div
          key={skeleton.id}
          className={cn(
            "transition-opacity duration-500",
            visibleSkeletons.has(skeleton.id) ? "opacity-100" : "opacity-0",
          )}
        >
          <DynamicSkeleton {...skeleton.props} />
        </div>
      ))}
    </div>
  );
}

/**
 * Hook for progressive loading state management
 */
export function useProgressiveLoading(
  items: any[],
  loading: boolean,
  options: {
    staggerDelay?: number;
    initialCount?: number;
  } = {},
) {
  const { staggerDelay = 200, initialCount = 3 } = options;
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  useEffect(() => {
    if (loading && items.length > initialCount) {
      setProgressiveLoading(true);

      // Simulate progressive loading completion
      const timer = setTimeout(() => {
        setProgressiveLoading(false);
      }, items.length * staggerDelay);

      return () => clearTimeout(timer);
    } else {
      setProgressiveLoading(false);
    }
  }, [loading, items.length, staggerDelay, initialCount]);

  return progressiveLoading;
}

/**
 * Predefined progressive skeleton configurations for common use cases
 */
export const PROGRESSIVE_SKELETONS = {
  dashboard: () => [
    {
      id: "hero",
      props: { component: "card" as const, variant: "dashboard" as const },
      delay: 0,
    },
    {
      id: "stats",
      props: {
        component: "card" as const,
        variant: "metric" as const,
        count: 3,
      },
      delay: 300,
    },
    {
      id: "activity",
      props: {
        component: "list" as const,
        variant: "mealplan" as const,
        count: 3,
      },
      delay: 600,
    },
  ],

  recipeGrid: (count: number = 8) => {
    return Array.from({ length: count }, (_, i) => ({
      id: `recipe-${i}`,
      props: { component: "card" as const, variant: "recipe" as const },
      delay: i * 150, // Stagger each recipe card
    }));
  },

  mealPlanList: (count: number = 5) => {
    return Array.from({ length: count }, (_, i) => ({
      id: `mealplan-${i}`,
      props: { component: "list" as const, variant: "mealplan" as const },
      delay: i * 200,
    }));
  },
} as const;
